import type React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import type {
  Session,
  User,
  SignInWithPasswordCredentials,
} from '@supabase/supabase-js';
import { supabase } from './index';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  login: (credentials: SignInWithPasswordCredentials) => Promise<void>;
  signUp: (credentials: SignInWithPasswordCredentials) => Promise<void>;
  logout: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    };

    getSession();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
      }
    );

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);

  const login = async (credentials: SignInWithPasswordCredentials) => {
    const { error } = await supabase.auth.signInWithPassword(credentials);
    if (error) {
      console.error('Error logging in:', error.message);
      // In a real app, you'd want to show this error to the user.
    }
  };

  const signUp = async (credentials: SignInWithPasswordCredentials) => {
    const { error } = await supabase.auth.signUp(credentials);
    if (error) {
      console.error('Error signing up:', error.message);
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
  };

  const signInWithGoogle = async () => {
    const redirectTo = process.env.SITE_URL ?? window.location.origin;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
      },
    });
    if (error) {
      console.error('Error signing in with Google:', error.message);
    }
  };

  const value = {
    session,
    user,
    login,
    signUp,
    logout,
    signInWithGoogle,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
